// WhatsApp in Connect (6.13, 4.9; docs/specs/WHATSAPP.md build step 3, migration 20261123100000): the shapes of
// crm_whatsapp_inbox and crm_whatsapp_thread, and the small pure helpers the inbox, the person's timeline and the
// approval cards share (time left in the 24-hour window, status ticks, what a refusal means).

export interface WaDraft {
  id: string;
  body: string;
  status: "draft" | "awaiting_approval";
  route?: "held" | "sampled" | "auto" | "no_gate" | null;
  created_at: string;
}

export interface WaOpenWindow {
  person_id: string;
  name: string | null;
  organisation_id?: string | null;
  opened_at: string;
  expires_at: string;
  minutes_left: number;
  handed_off_at?: string | null;
  handed_off_by?: string | null;
  handed_off_note?: string | null;
  last_in?: { id: string; at: string; body: string; intent?: string | null } | null;
  drafts: WaDraft[];
  answered: boolean;
}

export interface WaClosedWindow {
  person_id: string;
  name: string | null;
  expires_at: string;
  handed_off: boolean;
  drafts: number;
  /** Whether the approved follow-up template may go to them now (opt-in or a recent enquiry; never cold). */
  utility: boolean;
}

export interface WaInbox {
  open: WaOpenWindow[];
  closed: WaClosedWindow[];
  reply_mode: "draft" | "sample" | "auto";
  counts: { open: number; drafts: number; handed_off: number; closed: number };
}

export type WaStatus = "sent" | "delivered" | "read" | "played" | "failed";

export interface WaThreadMessage {
  id: string;
  at: string;
  direction: "in" | "out";
  body: string;
  /** person: they wrote; ogma: sent through Ogma; business_app: sent from the WhatsApp Business app (coexistence). */
  source: "person" | "ogma" | "business_app";
  status?: WaStatus | null;
  outbox_status?: string | null;
  step_id?: string | null;
  template?: string | null;
}

export interface WaThread {
  person_id: string;
  window?: { opened_at: string; expires_at: string; open: boolean; minutes_left: number } | null;
  handed_off_at?: string | null;
  messages: WaThreadMessage[];
}

/** "3h 20m left", "45m left", "closed". */
export function timeLeft(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || minutes <= 0) return "closed";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m}m left`;
  return m ? `${h}h ${m}m left` : `${h}h left`;
}

/** How urgent the window is: under an hour is urgent, under four hours soon. */
export function urgency(minutes: number): "urgent" | "soon" | "fine" {
  return minutes < 60 ? "urgent" : minutes < 240 ? "soon" : "fine";
}

/** WhatsApp's ticks, in words for screen readers and as marks for the eye. */
export function ticks(status: WaStatus | null | undefined, outboxStatus?: string | null): { mark: string; label: string; tone: "ok" | "bad" | "plain" } {
  switch (status) {
    case "read":
    case "played":
      return { mark: "✓✓", label: "Read", tone: "ok" };
    case "delivered":
      return { mark: "✓✓", label: "Delivered", tone: "plain" };
    case "sent":
      return { mark: "✓", label: "Sent", tone: "plain" };
    case "failed":
      return { mark: "!", label: "Not delivered", tone: "bad" };
    default:
      return outboxStatus === "approved" || outboxStatus === "sending"
        ? { mark: "…", label: "Waiting to send", tone: "plain" }
        : { mark: "", label: "", tone: "plain" };
  }
}

export const REPLY_MODE_LABEL: Record<WaInbox["reply_mode"], string> = {
  draft: "Every reply waits for you",
  sample: "Most replies go on their own once earned; some wait for you",
  auto: "Replies go on their own once earned",
};

export const SOURCE_LABEL: Record<WaThreadMessage["source"], string> = {
  person: "Them",
  ogma: "Ogma",
  business_app: "You (WhatsApp app)",
};

/** A reply's text problem before it is sent (the database checks again). */
export function replyProblem(body: string): string | null {
  if (!body.trim()) return "Write a message first.";
  if (body.length > 4096) return "A WhatsApp message is at most 4,096 characters.";
  return null;
}
